import { useChat } from "@appretio/chat";
import { useFeedbackApi } from "@appretio/feedback";
import { useCurrentUserPermissions, useTenantContext } from "@appretio/shared";
import { IconButton, Logo, NavigationMenu, UserMenu } from "@appretio/ui-kit";
import type { ComponentProps } from "react";
import { RouteObject, useNavigate } from "react-router-dom";
import { buildNavItems } from "../../utils/buildNavItems";
import {
  headerLogo,
  headerLogoWrapper,
  layoutHeader,
  navigationItem,
  navigationMenu,
  userBarRight,
  userMenu
} from "./AppLayout.css";

export interface AppHeaderProps {
  /*
   * Routes that drive the top navigation
   */
  navRoutes: RouteObject[];
  /*
   * Current location pathname (e.g., "/assortment/items")
   */
  pathname: string;
  /*
   * Current user's display name
   */
  userName: string;
  /*
   * Sign-out handler
   */
  onSignOut: () => void;
}

export function AppHeader({
  navRoutes,
  pathname,
  userName,
  onSignOut
}: AppHeaderProps) {
  const navigate = useNavigate();
  const { open: openChat } = useChat();
  // The API-only context: the header must not re-render on modal open/close.
  const feedbackApi = useFeedbackApi();
  const { data: permissions } = useCurrentUserPermissions();
  const adminItems: ComponentProps<typeof UserMenu>["items"] =
    permissions?.is_admin
      ? [
          {
            label: "Operations",
            icon: "wrench",
            onClick: () => navigate("/operations")
          }
        ]
      : [];

  const { currentOrg, organizations, isSwitching, switchOrg } =
    useTenantContext();
  // Tenant switching is a rare, identity-scoped action, so it lives inside
  // the user menu instead of spending header space — and only for users who
  // actually have somewhere to switch to.
  const showOrgSwitcher = organizations.length > 1;

  const sections = navRoutes
    .filter((r) =>
      import.meta.env.VITE_ENVIRONMENT === "production"
        ? !r.path?.includes("marketing") // Hide marketing module from navigation in prod, since it's not ready yet.
        : // (The routes are still accessible via direct URL, but they won't be linked from the app header.)
          true
    )
    .map((r) => buildNavItems(r, `/${r.path ?? ""}`, navigate));

  return (
    <header className={layoutHeader} data-testid="app-header">
      <div className={headerLogoWrapper}>
        <div className={headerLogo}>
          <Logo />
        </div>
      </div>

      <div className={navigationMenu}>
        {sections.map((sec, index) => {
          const groupKey = `group-${index} ${sec.groupLabel}`;

          const allItems = sec.items.flatMap((item) => [
            item,
            ...(item.children ?? [])
          ]);

          const selectedItem = allItems
            .filter((item) => pathname.startsWith(item.url))
            .sort((a, b) => b.url.length - a.url.length)[0];

          return (
            <div className={navigationItem} key={groupKey}>
              <NavigationMenu
                key={groupKey}
                items={sec.items}
                groupPlaceholder={sec.groupLabel}
                selectedItem={selectedItem}
              />
            </div>
          );
        })}
      </div>

      <div className={userBarRight}>
        {/* Low-key global affordance: every page, one click from a report.
            No ui-kit Tooltip exists yet — the native `title` carries the
            hover hint alongside the aria-label. */}
        <IconButton
          iconName="megaphone"
          variant="tertiary"
          size="md"
          aria-label="Send feedback"
          title="Send feedback"
          onClick={() => feedbackApi?.open()}
        />
        <IconButton
          iconName="chat"
          variant="tertiary"
          size="md"
          aria-label="Open chat"
          title="Open chat"
          onClick={openChat}
        />
        <div className={userMenu}>
          <UserMenu
            aria-label="User menu"
            userName={userName}
            subtitle={showOrgSwitcher ? currentOrg?.display_name : undefined}
            subMenus={
              showOrgSwitcher
                ? [
                    {
                      label: "Switch organization",
                      disabled: isSwitching,
                      items: organizations.map((org) => ({
                        label: org.display_name,
                        isActive: org.id === currentOrg?.id,
                        onSelect: () => {
                          // switchOrg rejects only if the redirect can't
                          // launch (the provider restores the prior
                          // selection); swallow it here so the rejection
                          // doesn't bubble as an unhandled promise.
                          void switchOrg(org.id).catch(() => {});
                        }
                      }))
                    }
                  ]
                : undefined
            }
            items={[
              {
                label: "Settings",
                icon: "settings",
                onClick: () => navigate("/settings")
              },
              ...adminItems,
              { label: "Sign out", icon: "log-out", onClick: onSignOut }
            ]}
          />
        </div>
      </div>
    </header>
  );
}
